import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaFingerprint,
  FaMapMarkerAlt,
  FaPause,
  FaPlay,
  FaReceipt,
  FaSearch,
  FaUserSecret,
  FaWhatsapp,
} from "react-icons/fa";
import { FiMenu, FiX } from "react-icons/fi";
import wefLogo from "../Assets/wef-detective-logo.png";
import mascot from "../Assets/wef-detective-mascot.png";
import detectiveTeam from "../Assets/wef-detective-team.png";
import registrationDetective from "../Assets/wef-registration-detective.png";
import finalCluePhone from "../Assets/wef-final-clue-phone.webp";
import appStoreQr from "../Assets/wef-app-store-qr.png";
import googlePlayQr from "../Assets/wef-google-play-qr.png";
import heroPoster from "../Assets/wef-detective-poster.png";
import heroVideo from "../Assets/wef-back-sep-2026.webm";
import niecLogo from "../Assets/niec-superstudent-logo.png";
import magnifierCursor from "../Assets/lup.png";
import { useEventCities } from "../queries/useEventCities";
import { registerEventParticipant, validateEventReferral } from "../API/service";
import "./WEFDetectiveLanding.css";

const emptyForm = {
  city: "",
  name: "",
  email: "",
  phone: "",
  source: "",
  referral: "",
};

const WITA_CITIES = new Set(["MAKASSAR", "DENPASAR"]);

const FREE_WORKSHOPS = [
  {
    id: "medan-ielts-ready",
    city: "Medan",
    title: "IELTS Ready: Your First Step to Study Abroad",
    speakerImage: "/images/workshops/medan-speaker-dossier-v1.webp",
    speakerName: "Indra Syahputra",
    speakerTitle: "English Teacher NIEC",
  },
  {
    id: "surabaya-ielts-simulation",
    city: "Surabaya",
    title: "Ace Your English From Classroom to Campus Abroadtudy Abroad IELTS Simulation: Test Your Readiness",
    speakerName: "Amalia Prita Intani",
    speakerTitle: "English Teacher NIEC",
    speakerImage: "/images/workshops/surabaya-speaker-dossier-v1.webp",
  },
  {
    id: "denpasar-global-journey",
    city: "Denpasar",
    title: "Ready for Your Global Journey: IELTS Simulation",
    speakerImage: "/images/workshops/denpasar-speaker-dossier-v1.webp",
    speakerName: "Anggraeni Agustin",
    speakerTitle: "English Teacher NIEC",
  },
  {
    id: "makassar-global-journey",
    city: "Makassar",
    title: "Think, Speak, Connect: English Skills for the World",
    speakerImage: "/images/workshops/makassar-speaker-dossier-v1.webp",
    speakerName: "A Nursusilawaty",
    speakerTitle: "English Teacher NIEC",
  },
];

function toCityLabel(city = "") {
  return city
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getTimezone(city = "") {
  return WITA_CITIES.has(city.toUpperCase())
    ? { suffix: "WITA", offset: "+08:00", iana: "Asia/Makassar" }
    : { suffix: "WIB", offset: "+07:00", iana: "Asia/Jakarta" };
}

function parseApiEventTime(timeEvent, city) {
  if (!timeEvent) return null;
  const { offset } = getTimezone(city);
  return new Date(`${timeEvent.replace(" ", "T")}${offset}`);
}

function parseDurationHours(description = "") {
  const match = String(description).match(/(\d+(?:[.,]\d+)?)\s*(?:hours?|jam)/i);
  return match ? Number(match[1].replace(",", ".")) : 0;
}

function formatApiTimeRange(timeEvent, city, durationHours = 0) {
  const start = parseApiEventTime(timeEvent, city);
  if (!start || Number.isNaN(start.getTime())) return "Jadwal segera diumumkan";
  const timezone = getTimezone(city);
  const formatter = new Intl.DateTimeFormat("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timezone.iana,
  });
  if (!durationHours) return `${formatter.format(start)} ${timezone.suffix}`;
  const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
  return `${formatter.format(start)} – ${formatter.format(end)} ${timezone.suffix}`;
}

function normalizeEventLocations(locations = []) {
  return locations.map((location) => {
    const cityKey = location.kabupaten?.nama_kabkota?.trim().toUpperCase() || "";
    const start = parseApiEventTime(location.time_event, cityKey);
    const durationHours = parseDurationHours(location.description);
    const end = start && durationHours ? new Date(start.getTime() + durationHours * 60 * 60 * 1000) : start;
    const timezone = getTimezone(cityKey);

    return {
      id: String(location.kabupaten?.id_kabkota || location.kode_kabkota || location.id),
      locationId: location.id,
      cityKey,
      city: toCityLabel(cityKey),
      date: start?.toISOString() || "",
      startTimestamp: start?.getTime() || 0,
      endTimestamp: end?.getTime() || 0,
      dateLabel: start
        ? new Intl.DateTimeFormat("id-ID", {
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: timezone.iana,
          }).format(start)
        : "Jadwal segera diumumkan",
      time: formatApiTimeRange(location.time_event, cityKey, durationHours),
      venue: location.place || "Lokasi segera diumumkan",
      address: location.alamat || "Alamat segera diumumkan",
    };
  });
}

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const NATIVE_CURSOR_TARGETS = [
  "input",
  "textarea",
  "select",
  "option",
  "[contenteditable]:not([contenteditable='false'])",
  "[disabled]",
  "[aria-disabled='true']",
].join(",");

function MagnifierCursor() {
  const cursorRef = useRef(null);
  const activeClueRef = useRef(null);
  const foundCluesRef = useRef(new Set());
  const [activeClue, setActiveClue] = useState(null);
  const [foundClueCount, setFoundClueCount] = useState(0);

  useEffect(() => {
    const cursor = cursorRef.current;
    const landing = cursor?.closest(".detective-landing");
    if (!cursor || !landing) return undefined;

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frameId = null;
    let position = { x: 0, y: 0 };

    const canShow = () => finePointer.matches && !reducedMotion.matches;

    const clearActiveClue = () => {
      if (!activeClueRef.current) return;
      activeClueRef.current.classList.remove("is-magnified");
      activeClueRef.current = null;
      cursor.classList.remove("is-over-clue", "is-clue-left");
      setActiveClue(null);
    };

    const hideCursor = () => {
      landing.classList.remove("has-magnifier-cursor");
      cursor.classList.remove("is-visible", "is-pressed");
      clearActiveClue();
    };

    const updateClue = () => {
      const hotspots = landing.querySelectorAll("[data-treasure-clue]");
      let nearbyClue = null;

      for (const hotspot of hotspots) {
        const rect = hotspot.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        if (Math.hypot(position.x - centerX, position.y - centerY) <= 64) {
          nearbyClue = hotspot;
          break;
        }
      }

      if (nearbyClue === activeClueRef.current) return;
      clearActiveClue();
      if (!nearbyClue) return;

      activeClueRef.current = nearbyClue;
      nearbyClue.classList.add("is-magnified", "is-found");
      cursor.classList.add("is-over-clue");
      cursor.classList.toggle("is-clue-left", position.x > window.innerWidth - 310);

      const clue = {
        id: nearbyClue.dataset.clueId,
        number: nearbyClue.dataset.clueNumber,
        text: nearbyClue.dataset.clueText,
      };
      setActiveClue(clue);

      if (!foundCluesRef.current.has(clue.id)) {
        foundCluesRef.current.add(clue.id);
        setFoundClueCount(foundCluesRef.current.size);
      }
    };

    const renderPosition = () => {
      cursor.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
      updateClue();
      frameId = null;
    };

    const handlePointerMove = (event) => {
      if (!canShow() || event.pointerType === "touch") {
        hideCursor();
        return;
      }

      const nativeTarget =
        event.target instanceof Element &&
        Boolean(event.target.closest(NATIVE_CURSOR_TARGETS));

      if (nativeTarget) {
        hideCursor();
        return;
      }

      position = { x: event.clientX, y: event.clientY };
      landing.classList.add("has-magnifier-cursor");
      cursor.classList.add("is-visible");

      if (frameId === null) {
        frameId = window.requestAnimationFrame(renderPosition);
      }
    };

    const handlePointerDown = (event) => {
      if (cursor.classList.contains("is-visible") && event.button === 0) {
        cursor.classList.add("is-pressed");
      }
    };

    const handlePointerUp = () => cursor.classList.remove("is-pressed");
    const handlePreferenceChange = () => {
      if (!canShow()) hideCursor();
    };

    landing.addEventListener("pointermove", handlePointerMove, { passive: true });
    landing.addEventListener("pointerleave", hideCursor);
    landing.addEventListener("pointerdown", handlePointerDown, { passive: true });
    window.addEventListener("pointerup", handlePointerUp, { passive: true });
    window.addEventListener("blur", hideCursor);
    finePointer.addEventListener("change", handlePreferenceChange);
    reducedMotion.addEventListener("change", handlePreferenceChange);

    return () => {
      landing.removeEventListener("pointermove", handlePointerMove);
      landing.removeEventListener("pointerleave", hideCursor);
      landing.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("blur", hideCursor);
      finePointer.removeEventListener("change", handlePreferenceChange);
      reducedMotion.removeEventListener("change", handlePreferenceChange);
      landing.classList.remove("has-magnifier-cursor");
      clearActiveClue();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <div ref={cursorRef} className="magnifier-cursor" aria-hidden="true">
      <span className="magnifier-cursor-lens" />
      <img src={magnifierCursor} alt="" draggable="false" />
      {activeClue && (
        <span className="magnifier-clue-card">
          <small>Petunjuk {activeClue.number} ditemukan · {foundClueCount}/3</small>
          <strong>{activeClue.text}</strong>
        </span>
      )}
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 48);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header className={`case-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="case-shell case-header-inner">
        <button className="case-brand" onClick={() => goTo("home")} aria-label="World Education Festival home">
          <img src={wefLogo} alt="World Education Festival" />
        </button>

        <nav className={`case-nav ${open ? "is-open" : ""}`} aria-label="Primary navigation">
          <button onClick={() => goTo("home")}>Beranda</button>
          <button onClick={() => goTo("jadwal")}>Jadwal</button>
          <button onClick={() => goTo("daftar")}>Daftar</button>
          <button onClick={() => goTo("workshop")}>Workshop</button>
        </nav>

        <button className="case-button case-button-small header-cta" onClick={() => goTo("daftar")}>
          Daftar Gratis
        </button>
        <button
          className="menu-button"
          type="button"
          aria-label={open ? "Tutup menu" : "Buka menu"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <FiX /> : <FiMenu />}
        </button>
      </div>
    </header>
  );
}

function Hero({ events }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  const toggleVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--pointer-x", `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty("--pointer-y", `${event.clientY - rect.top}px`);
  };

  return (
    <section id="home" className="case-hero" onPointerMove={handlePointerMove}>
      <video
        ref={videoRef}
        className="case-hero-video"
        src={heroVideo}
        poster={heroPoster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
      />
      <div className="case-hero-shade" />
      <div className="case-pointer-light" aria-hidden="true" />
      <span
        className="treasure-hotspot treasure-hotspot-hero"
        data-treasure-clue
        data-clue-id="first-step"
        data-clue-number="01"
        data-clue-text="Jejak pertama: tujuan besar dimulai dari satu langkah kecil."
        aria-hidden="true"
      />

      <div className="case-shell case-hero-layout">
        <motion.figure
          className="case-hero-team"
          initial={{ opacity: 0, x: -44 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.12 }}
        >
          <img
            src={detectiveTeam}
            alt="Tiga pencari kampus bergaya detektif membawa kaca pembesar"
          />
        </motion.figure>

        <motion.div
          className="case-hero-copy"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75 }}
        >
          <img className="case-event-logo" src={wefLogo} alt="World Education Festival 2026" />
          <p className="case-eyebrow"><FaSearch /> Festival studi luar negeri gratis</p>
          <h1>Bongkar Jalur Kuliah di Luar Negeri</h1>
          <p className="case-hero-lead">
            Temui perwakilan kampus, cek peluang beasiswa, dan susun rencana studimu bersama konsultan berpengalaman.
          </p>

          <div className="case-hero-actions">
            <button className="case-button" onClick={() => scrollToSection("daftar")}>
              Daftar Gratis Sekarang <FaArrowRight />
            </button>
            <button className="case-button case-button-ghost" onClick={() => scrollToSection("jadwal")}>
              Lihat {events.length || "Semua"} Kota
            </button>
          </div>
          <ul className="case-trust-list" aria-label="Keunggulan acara">
            <li><FaCheck /> Gratis</li>
            <li><FaCheck /> {events.length ? `${events.length} kota` : "Multi-kota"}</li>
            <li><FaCheck /> Konsultasi langsung</li>
          </ul>
        </motion.div>

        <motion.img
          className="case-mascot"
          src={mascot}
          alt="Maskot detektif World Education Festival"
          initial={{ opacity: 0, x: 34 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
      </div>

      <button className="video-control" onClick={toggleVideo} aria-label={playing ? "Jeda video latar" : "Putar video latar"}>
        {playing ? <FaPause /> : <FaPlay />}
        <span>{playing ? "Jeda latar" : "Putar latar"}</span>
      </button>
    </section>
  );
}

function useCountdown(events) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return useMemo(() => {
    if (!events.length) return { status: "empty", event: null };

    const ongoing = events.find((event) => now >= event.startTimestamp && now < event.endTimestamp);
    if (ongoing) return { status: "ongoing", event: ongoing };

    const next = events.find((event) => now < event.startTimestamp);
    if (!next) return { status: "finished", event: null };

    const remaining = Math.max(0, next.startTimestamp - now);
    return {
      status: "upcoming",
      event: next,
      time: {
        days: Math.floor(remaining / 86400000),
        hours: Math.floor((remaining % 86400000) / 3600000),
        minutes: Math.floor((remaining % 3600000) / 60000),
        seconds: Math.floor((remaining % 60000) / 1000),
      },
    };
  }, [events, now]);
}

function Countdown({ events, isLoading, isError, onRetry }) {
  const countdown = useCountdown(events);

  return (
    <section className="countdown-strip" aria-label="Countdown acara">
      <div className="case-shell countdown-card">
        <div className="countdown-copy">
          <span>Case start</span>
          {countdown.status === "upcoming" && (
            <>
              <p>Kasus dimulai dari {countdown.event.city}</p>
              <h2>{countdown.event.dateLabel}</h2>
              <small><FaMapMarkerAlt /> {countdown.event.venue}</small>
            </>
          )}
          {countdown.status === "ongoing" && <h2>WEF {countdown.event.city} sedang berlangsung!</h2>}
          {countdown.status === "finished" && <h2>World Education Festival 2026 telah selesai</h2>}
          {isLoading && <h2>Memuat petunjuk jadwal acara…</h2>}
          {!isLoading && isError && (
            <div className="case-data-error">
              <h2>Jadwal belum berhasil dimuat</h2>
              <button type="button" onClick={() => onRetry()}>Coba lagi</button>
            </div>
          )}
          {!isLoading && !isError && countdown.status === "empty" && <h2>Jadwal acara segera diumumkan</h2>}
        </div>

        {countdown.status === "upcoming" && (
          <div className="countdown-numbers" role="timer" aria-label={`Hitung mundur ${countdown.time.days} hari ${countdown.time.hours} jam`}>
            {[
              [countdown.time.days, "Hari"],
              [countdown.time.hours, "Jam"],
              [countdown.time.minutes, "Menit"],
              [countdown.time.seconds, "Detik"],
            ].map(([value, label]) => (
              <div key={label}>
                <strong>{String(value).padStart(2, "0")}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        <button className="countdown-link" onClick={() => scrollToSection("jadwal")}>
          Lihat detail jadwal <FaArrowRight />
        </button>
      </div>
    </section>
  );
}

function SectionHeading({ label, title, description }) {
  return (
    <div className="section-heading">
      <p>{label}</p>
      <h2>{title}</h2>
      <span>{description}</span>
    </div>
  );
}

function Schedule({ events, selectedCity, onSelect, isLoading, isError, onRetry }) {
  const nextEventId = events.find((event) => Date.now() < event.startTimestamp)?.id;

  return (
    <section id="jadwal" className="case-section schedule-section">
      <span
        className="treasure-hotspot treasure-hotspot-schedule"
        data-treasure-clue
        data-clue-id="city-code"
        data-clue-number="02"
        data-clue-text="Kode kota: pilih lokasi yang paling dekat dengan rencanamu."
        aria-hidden="true"
      />
      <div className="case-shell">
        <SectionHeading
          label="Case File 01 — Pilih Kota"
          title="Jadwal World Education Festival"
          description="Pilih kota dan waktu yang paling cocok untuk memulai rencana studi luar negerimu."
        />
        {isLoading && <div className="case-data-state">Menyusun berkas jadwal dari server…</div>}
        {!isLoading && isError && (
          <div className="case-data-state case-data-error">
            <p>Data kota acara tidak dapat dimuat.</p>
            <button type="button" className="case-button" onClick={() => onRetry()}>Coba Muat Lagi</button>
          </div>
        )}
        {!isLoading && !isError && events.length === 0 && <div className="case-data-state">Belum ada jadwal kota yang tersedia.</div>}
        <div className="schedule-grid">
          {events.map((event, index) => {
            const isFullyRegistered = event.cityKey === "MEDAN" || String(event.id) === "146" || String(event.locationId) === "1937";
            return (
              <motion.article
                className={`schedule-card ${selectedCity === event.id ? "is-selected" : ""} ${isFullyRegistered ? "is-fully-registered" : ""}`}
                key={event.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="schedule-card-top">
                  <span>City case file</span>
                  {isFullyRegistered ? (
                    <strong className="badge-fully-registered">Fully Registered</strong>
                  ) : event.id === nextEventId ? (
                    <strong>Kasus Terdekat</strong>
                  ) : null}
                </div>
                <h3>{event.city}</h3>
                <ul>
                  <li><FaCalendarAlt /><span>{event.dateLabel}</span></li>
                  <li><FaClock /><span>{event.time}</span></li>
                  <li><FaMapMarkerAlt /><span><b>{event.venue}</b><small>{event.address}</small></span></li>
                </ul>
                <button
                  className="case-button case-button-wide"
                  onClick={() => !isFullyRegistered && onSelect(event.id)}
                  disabled={isFullyRegistered}
                >
                  {isFullyRegistered ? (
                    <>Fully Registered</>
                  ) : selectedCity === event.id ? (
                    <><FaCheck /> Jadwal Dipilih</>
                  ) : (
                    <>Pilih Jadwal Ini <FaArrowRight /></>
                  )}
                </button>
              </motion.article>
            );
          })}
        </div>
        <p className="schedule-note">* Beberapa detail lokasi dan waktu dapat diperbarui menjelang acara.</p>
      </div>
    </section>
  );
}

function RegistrationForm({ events, selectedCity, onCityChange, eventDataLoading }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...emptyForm, city: selectedCity });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setForm((current) => ({ ...current, city: selectedCity }));
  }, [selectedCity]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setSubmitError("");
    if (name === "city") onCityChange(value, false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.city) nextErrors.city = "Pilih jadwal kota terlebih dahulu.";
    if (!form.name.trim()) nextErrors.name = "Nama lengkap wajib diisi.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Masukkan alamat email yang valid.";
    if (!/^\d{8,15}$/.test(form.phone.replace(/\D/g, ""))) nextErrors.phone = "Masukkan nomor WhatsApp yang valid.";
    if (!form.source) nextErrors.source = "Pilih sumber informasi WEF.";
    setErrors(nextErrors);
    setSubmitError("");
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const referralResult = await validateEventReferral(form.referral.trim());
      if (referralResult?.data !== 2) {
        setErrors((current) => ({ ...current, referral: referralResult?.message || "Kode referral tidak valid." }));
        return;
      }

      const digits = form.phone.replace(/\D/g, "").replace(/^0+/, "");
      const phone = digits.startsWith("62") ? digits : `62${digits}`;
      await registerEventParticipant({
        id_kabkota: Number(form.city),
        name: form.name.trim(),
        email: form.email.trim(),
        phone,
        source: form.source,
        kode_referal: form.referral.trim(),
        keterangan: "",
        sekolah: "",
        utm: new URLSearchParams(window.location.search).get("source"),
      });
      navigate("/claim-ticket", {
        state: { registrationEmail: form.email.trim() },
      });
    } catch (error) {
      setSubmitError(error?.response?.data?.message || "Pendaftaran belum berhasil dikirim. Silakan coba lagi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedEvent = events.find((e) => String(e.id) === String(form.city));
  const isSelectedCityFullyRegistered = selectedEvent && (selectedEvent.cityKey === "MEDAN" || String(selectedEvent.id) === "146" || String(selectedEvent.locationId) === "1937");

  return (
    <section id="daftar" className="case-section form-section">
      <div className="case-shell form-layout">
        <div className="form-story">
          <SectionHeading
            label="Case File 02 — Daftar Gratis"
            title="Amankan Tempatmu di WEF 2026"
            description="Isi data berikut agar tim NIEC dapat mengirimkan detail acara melalui WhatsApp."
          />
          <img
            className="form-detective"
            src={registrationDetective}
            alt="Detektif WEF membawa dokumen pendaftaran"
          />
          <div className="form-tip">
            <FaSearch />
            <div><strong>Petunjuk detektif</strong><span>Pilih jadwal lebih dulu agar berkasmu langsung terhubung ke kota tujuan.</span></div>
          </div>
        </div>

        <form className="case-form" onSubmit={handleSubmit} noValidate>
          {submitError && <div className="form-api-error" role="alert">{submitError}</div>}

          <label className="form-field form-field-full">
            <span>Pilih Jadwal Kota *</span>
            <select name="city" value={form.city} onChange={updateField} aria-invalid={Boolean(errors.city)} disabled={eventDataLoading || events.length === 0}>
              <option value="">— Pilih kota —</option>
              {events.map((event) => {
                const isFull = event.cityKey === "MEDAN" || String(event.id) === "146" || String(event.locationId) === "1937";
                return (
                  <option value={event.id} key={event.id} disabled={isFull}>
                    {event.city} — {event.dateLabel}{isFull ? " (Fully Registered)" : ""}
                  </option>
                );
              })}
            </select>
            {errors.city && <small className="field-error">{errors.city}</small>}
            {isSelectedCityFullyRegistered && (
              <small className="field-error text-danger d-block mt-1">
                Pendaftaran untuk kota Medan telah memenuhi kuota (Fully Registered).
              </small>
            )}
          </label>

          <div className="form-grid">
            <label className="form-field">
              <span>Nama Lengkap *</span>
              <input name="name" value={form.name} onChange={updateField} placeholder="cth: Rara Wijaya" aria-invalid={Boolean(errors.name)} />
              {errors.name && <small className="field-error">{errors.name}</small>}
            </label>
            <label className="form-field">
              <span>Email *</span>
              <input name="email" type="email" value={form.email} onChange={updateField} placeholder="nama@email.com" aria-invalid={Boolean(errors.email)} />
              {errors.email && <small className="field-error">{errors.email}</small>}
            </label>
            <label className="form-field">
              <span>Nomor WhatsApp *</span>
              <div className="phone-field"><b>+62</b><input name="phone" inputMode="numeric" value={form.phone} onChange={updateField} placeholder="81234567890" aria-invalid={Boolean(errors.phone)} /></div>
              {errors.phone && <small className="field-error">{errors.phone}</small>}
            </label>
            <label className="form-field">
              <span>Tahu WEF dari mana? *</span>
              <select name="source" value={form.source} onChange={updateField} aria-invalid={Boolean(errors.source)}>
                <option value="">— Pilih sumber —</option>
                <option>Instagram</option><option>Facebook</option><option>TikTok</option>
                <option value="Sekolah">Kunjungan Sekolah</option><option value="Konsultan NIEC">Walk-in/Konsultan NIEC</option>
              </select>
              {errors.source && <small className="field-error">{errors.source}</small>}
            </label>
          </div>

          <label className="form-field form-field-full">
            <span>Kode Referral <em>(opsional)</em></span>
            <input name="referral" value={form.referral} onChange={updateField} placeholder="cth: AMB2026" />
            <small>Isi hanya jika memiliki kode undangan ambassador.</small>
            {errors.referral && <small className="field-error">{errors.referral}</small>}
          </label>

          <button className="case-button case-button-submit" type="submit" disabled={isSubmitting || eventDataLoading || events.length === 0 || isSelectedCityFullyRegistered}>
            <FaReceipt /> {isSubmitting ? "Memulai Case..." : isSelectedCityFullyRegistered ? "Fully Registered" : "Daftar Sekarang"}
          </button>
          <p className="privacy-note">Data kamu hanya digunakan oleh tim NIEC untuk menginformasikan acara WEF 2026.</p>
        </form>
      </div>
    </section>
  );
}

function FreeWorkshops({ events, onSelectCity }) {
  const [activeCity, setActiveCity] = useState("all");
  const cities = useMemo(
    () => [...new Set(FREE_WORKSHOPS.map((workshop) => workshop.city))],
    [],
  );
  const visibleWorkshops = activeCity === "all"
    ? FREE_WORKSHOPS
    : FREE_WORKSHOPS.filter((workshop) => workshop.city === activeCity);

  const selectWorkshop = (workshop) => {
    const matchingEvent = events.find(
      (event) => event.city.trim().toLowerCase() === workshop.city.toLowerCase(),
    );
    if (matchingEvent) {
      onSelectCity(matchingEvent.id);
      return;
    }
    scrollToSection("daftar");
  };

  return (
    <section id="workshop" className="case-section workshop-section">
      <div className="case-shell">
        <div className="workshop-heading-row">
          <SectionHeading
            label="Special Case File — Free Workshop"
            title="Buka Berkas Workshop Gratis"
            description="Pilih kota, uji kesiapan IELTS-mu, dan temukan langkah berikutnya menuju studi di luar negeri."
          />
          <div className="workshop-file-count" aria-label={`${FREE_WORKSHOPS.length} workshop tersedia`}>
            <span>Berkas aktif</span>
            <strong>{String(FREE_WORKSHOPS.length).padStart(2, "0")}</strong>
          </div>
        </div>

        <div className="workshop-tabs" aria-label="Filter workshop berdasarkan kota">
          <button
            type="button"
            className={activeCity === "all" ? "is-active" : ""}
            onClick={() => setActiveCity("all")}
            aria-pressed={activeCity === "all"}
          >
            Semua Kota
          </button>
          {cities.map((city) => (
            <button
              type="button"
              className={activeCity === city ? "is-active" : ""}
              key={city}
              onClick={() => setActiveCity(city)}
              aria-pressed={activeCity === city}
            >
              {city}
            </button>
          ))}
        </div>

        <div className="workshop-grid">
          {visibleWorkshops.map((workshop, index) => (
            <motion.article
              className="workshop-card"
              key={workshop.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.08 }}
            >
              <div className="speaker-photo">
                {workshop.speakerImage ? (
                  <img src={workshop.speakerImage} alt={`${workshop.speakerName || "Pembicara"} — workshop ${workshop.city}`} width={1619} height={971} loading="lazy" />
                ) : (
                  <div className="speaker-classified" role="img" aria-label="Foto pembicara masih dirahasiakan">
                    <FaUserSecret className="speaker-classified-profile" aria-hidden="true" />
                    <span className="speaker-classified-stamp">Classified</span>
                    <small>Speaker dossier</small>
                  </div>
                )}
              </div>

              <div className="workshop-copy">
                <div className="workshop-meta-top">
                  <span><FaMapMarkerAlt /> {workshop.city}</span>
                  <b>Free Workshop</b>
                </div>
                <h3>{workshop.title}</h3>
                <div className="speaker-status">
                  <FaFingerprint aria-hidden="true" />
                  <span>
                    <strong>{workshop.speakerName || "Identitas pembicara"}</strong>
                    <small>{workshop.speakerTitle || "Akan segera dibuka"}</small>
                  </span>
                </div>
                <button
                  type="button"
                  className="case-button case-button-wide workshop-register"
                  onClick={() => selectWorkshop(workshop)}
                >
                  Daftar Workshop Gratis <FaArrowRight />
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="workshop-expansion-note">
          <FaSearch aria-hidden="true" />
          <p><strong>Penyelidikan masih berlanjut.</strong><span>Kota dan workshop baru akan ditambahkan ke berkas ini.</span></p>
        </div>
      </div>
    </section>
  );
}

function Closing() {
  const storeLinks = [
    {
      name: "App Store",
      href: "https://apps.apple.com/us/app/super-student/id1614354792",
      qr: appStoreQr,
    },
    {
      name: "Google Play",
      href: "https://play.google.com/store/apps/details?id=io.niec.superstudent",
      qr: googlePlayQr,
    },
  ];

  return (
    <section className="closing-section">
      <span
        className="treasure-hotspot treasure-hotspot-closing"
        data-treasure-clue
        data-clue-id="final-ticket"
        data-clue-number="03"
        data-clue-text="Clue terakhir: download Super Student dan klaim tiketmu di menu Event."
        aria-hidden="true"
      />
      <div className="case-shell closing-card">
        <div className="closing-visual" aria-hidden="true">
          <img src={finalCluePhone} alt="" />
        </div>

        <div className="closing-content">
          <span className="closing-kicker">Final clue</span>
          <h2>Download Super Student & klaim tiketmu.</h2>
          <p className="closing-lead">Masuk dengan email pendaftaran WEF, lalu buka menu <strong>Event</strong> untuk mengambil e-ticket.</p>
        </div>

        <div className="closing-downloads">
          <strong>Scan atau klik QR</strong>
          <div className="closing-qr-grid">
            {storeLinks.map((store) => (
              <a
                className="closing-qr-card"
                href={store.href}
                target="_blank"
                rel="noreferrer"
                aria-label={`Download Super Student di ${store.name}`}
                key={store.name}
              >
                <img src={store.qr} alt={`QR download Super Student di ${store.name}`} />
                <span>{store.name} <FaArrowRight /></span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ events }) {
  return (
    <footer className="case-footer">
      <div className="case-shell footer-grid">
        <div><img src={niecLogo} alt="NIEC dan Super Student" /><p>World Education Festival 2026 membantu pelajar Indonesia menemukan rute studi internasional yang tepat.</p></div>
        <div><h3>Navigasi</h3><button onClick={() => scrollToSection("home")}>Beranda</button><button onClick={() => scrollToSection("jadwal")}>Jadwal</button><button onClick={() => scrollToSection("daftar")}>Daftar</button><button onClick={() => scrollToSection("workshop")}>Workshop</button></div>
        <div><h3>Event</h3><p>September 2026</p><p>{events.length ? events.map((event) => event.city).join(" · ") : "Jadwal kota segera diumumkan"}</p></div>
      </div>
      <div className="case-shell footer-bottom">© 2026 NIEC — World Education Festival. All rights reserved.</div>
    </footer>
  );
}

export default function WEFDetectiveLanding() {
  const { data: eventLocations = [], isLoading, isError, refetch } = useEventCities();
  const events = useMemo(() => normalizeEventLocations(eventLocations), [eventLocations]);
  const [selectedCity, setSelectedCity] = useState("");

  useEffect(() => {
    if (events.length) {
      const isSelectedValid = events.some((event) => event.id === selectedCity && event.cityKey !== "MEDAN" && String(event.id) !== "146" && String(event.locationId) !== "1937");
      if (!isSelectedValid) {
        const availableEvent = events.find((event) => event.cityKey !== "MEDAN" && String(event.id) !== "146" && String(event.locationId) !== "1937");
        if (availableEvent) {
          setSelectedCity(availableEvent.id);
        } else {
          setSelectedCity(events[0].id);
        }
      }
    }
  }, [events, selectedCity]);

  const selectCity = (city, scroll = true) => {
    setSelectedCity(city);
    if (scroll) window.setTimeout(() => scrollToSection("daftar"), 80);
  };

  return (
    <div className="detective-landing">
      <MagnifierCursor />
      <Header />
      <main>
        <Hero events={events} />
        <Countdown events={events} isLoading={isLoading} isError={isError} onRetry={refetch} />
        <Schedule events={events} selectedCity={selectedCity} onSelect={selectCity} isLoading={isLoading} isError={isError} onRetry={refetch} />
        <RegistrationForm events={events} selectedCity={selectedCity} onCityChange={selectCity} eventDataLoading={isLoading} />
        <FreeWorkshops events={events} onSelectCity={selectCity} />
        <Closing />
      </main>
      <Footer events={events} />
    </div>
  );
}
